/**
 * Helpers for keeping the URL's entity segment in sync with the active V2
 * division.
 *
 * Why: Beacon (and Omni) URLs are shaped `/client/:beaconClientId/...`. The
 * `:beaconClientId` is per-division — switching to another division means a
 * different default vendor (e.g. `/client/915` vs King Arthur `/client/398`).
 * After a division switch the in-app dropdown previously did
 * `window.location.reload()`, which kept the stale id in the URL. These
 * helpers compute the new URL so the caller can navigate instead of reloading.
 *
 * Some URLs are bound to a division-specific entity (e.g. `/segment/:id`,
 * `/category/:id`, `?tab=advertising`). The new division may not have access
 * to that entity, so we redirect to `/` and let the router land on the new
 * division's default page.
 */

const CLIENT_PATH_PATTERN = /^\/client\/[^/]+(.*)$/;

/**
 * Pathname segments and query-string fragments that bind a URL to a specific
 * entity of the OUTGOING division. Substring matches against `pathname` are
 * intentional — these are top-level routes (e.g. `/segment/12345`), not
 * nested under `/client/:id`.
 */
const ENTITY_BOUND_PATH_SEGMENTS = [
  '/category/',
  '/subcategory/',
  '/segment/',
  '/topic/',
  '/brand/'
];
const ENTITY_BOUND_QUERY_FRAGMENTS = ['tab=advertising', 'profileId=', 'projectId='];

const isEntityBoundUrl = (pathname: string, search: string): boolean => {
  if (ENTITY_BOUND_PATH_SEGMENTS.some((segment) => pathname.includes(segment))) {
    return true;
  }
  return ENTITY_BOUND_QUERY_FRAGMENTS.some((fragment) => search.includes(fragment));
};

/**
 * Replace the `:id` in a `/client/:id...` path with `newClientId`, preserving
 * any tail segments. Returns `null` when the pathname is not a `/client/:id`
 * path (so callers know to fall back to a plain reload).
 */
export const rewriteClientPath = (
  pathname: string,
  newClientId: number | string
): string | null => {
  const match = pathname.match(CLIENT_PATH_PATTERN);
  if (!match) return null;
  const tail = match[1];
  return `/client/${newClientId}${tail}`;
};

/**
 * Build the post-switch URL by rewriting the current `window.location` against
 * the new division's default Beacon client id. Preserves search and hash —
 * UNLESS the URL is bound to a division-specific entity, in which case we
 * land on `/` so the router routes to the new division's default page.
 *
 * Returns `null` when no rewrite is needed (caller should reload() instead).
 *
 * `newClientId` is optional: pass `null` for divisions without a default
 * Beacon client (Atlas-only). Entity-bound URLs are reset regardless.
 */
export const buildPostSwitchUrl = (
  newClientId: number | string | null | undefined
): string | null => {
  const { pathname, search, hash } = window.location;

  if (isEntityBoundUrl(pathname, search)) return '/';

  if (newClientId == null) return null;
  const newPath = rewriteClientPath(pathname, newClientId);
  if (newPath === null) return null;
  return `${newPath}${search}${hash}`;
};
